import React, { useEffect, useRef, useState } from 'react';
import { Eye, Share2, Bookmark } from 'lucide-react';
import {
  LiveEvent, LiveViewersPayload, ReactionCounts, ReactionKey,
  REACTION_KEYS, REACTION_EMOJI, REACTION_LABEL, UPLOADS_BASE
} from '../../services/liveService';

const HERO_REACTIONS: ReactionKey[] = ['like', 'love', 'clap', 'celebrate', 'wow'];

interface Props {
  live: LiveEvent;
  viewers?: LiveViewersPayload;
  reactionsCounts: ReactionCounts;
  // myReaction non e\` piu\` usato: l'hero e\` LinkedIn-style infinite (no toggle, no highlight).
  // Lasciato per compat con il tipo Props di consumer storici.
  myReaction?: ReactionKey | null;
  isAuthenticated: boolean;
  onReact?: (emoji: ReactionKey) => void;
  /** Queue di emoji da spawnare come "floating". Ogni nuovo elemento => 1 spawn. */
  floatQueue?: { emoji: ReactionKey; id: number }[];
  onFloatConsumed?: (id: number) => void;
}

const formatElapsed = (sec: number) => {
  const h = Math.floor(sec / 3600).toString().padStart(2, '0');
  const m = Math.floor((sec % 3600) / 60).toString().padStart(2, '0');
  const s = (sec % 60).toString().padStart(2, '0');
  return `${h}:${m}:${s}`;
};

const compact = (n: number) =>
  n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k' : n.toLocaleString('it-IT');

const coverSrc = (url?: string | null) => {
  if (!url) return '';
  return /^https?:\/\//.test(url) ? url : `${UPLOADS_BASE}${url}`;
};

const LiveHero: React.FC<Props> = ({
  live, viewers, reactionsCounts, isAuthenticated, onReact,
  floatQueue, onFloatConsumed
}) => {
  const [elapsed, setElapsed] = useState(0);
  const floatRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const base = live.accumulatedSeconds || 0;
    // In pausa/conclusa/bozza: mostra il totale accumulato congelato (no interval, no tempo di pausa).
    if (live.status !== 'live' || !live.startedAt) {
      setElapsed(base);
      return;
    }
    // In onda: totale accumulato + segmento corrente (da startedAt).
    const startMs = new Date(live.startedAt).getTime();
    const tick = () => setElapsed(base + Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [live.status, live.startedAt, live.accumulatedSeconds]);

  // Spawn floating emoji animati sul layer
  const spawnFloat = (emojiKey: ReactionKey) => {
    const layer = floatRef.current;
    if (!layer) return;
    const el = document.createElement('span');
    el.className = 'float-emo';
    el.textContent = REACTION_EMOJI[emojiKey];
    const x = 30 + Math.random() * Math.max(60, layer.clientWidth - 60);
    el.style.left = x + 'px';
    el.style.setProperty('--dx1', (Math.random() * 40 - 20) + 'px');
    el.style.setProperty('--dx2', (Math.random() * 120 - 60) + 'px');
    layer.appendChild(el);
    setTimeout(() => el.remove(), 2700);
  };

  // Drena la queue di floating emoji: spawna ogni nuovo elemento e chiama onConsumed
  // (no perdita su React batching, ogni id viene processato esattamente una volta).
  useEffect(() => {
    if (!floatQueue || floatQueue.length === 0) return;
    floatQueue.forEach(f => {
      spawnFloat(f.emoji);
      onFloatConsumed?.(f.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floatQueue]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: live.title, url }); return; } catch {}
    }
    try { await navigator.clipboard.writeText(url); } catch {}
  };

  // LinkedIn-style: ogni click ADD (no toggle). Spawn animato sul click + via socket.
  const handleReact = (k: ReactionKey) => {
    if (!isAuthenticated || !onReact || live.status !== 'live') return;
    onReact(k);
    for (let i = 0; i < 3; i++) setTimeout(() => spawnFloat(k), i * 110);
  };

  const viewersCount = viewers?.count ?? 0;
  const recent = viewers?.recent || [];
  const stackAvatars = recent.slice(0, 3).map(v => v.initials || (v.name || '··').slice(0, 2).toUpperCase());
  const totalReactions = REACTION_KEYS.reduce((s, k) => s + (reactionsCounts[k] || 0), 0);
  const kicker = (live.linkedEventId && (live.linkedEventId as any).nome) || 'DIRETTA UNASCA';

  return (
    <section className="hero">
      <div className="hero-media">
        {live.coverImage ? (
          <img src={coverSrc(live.coverImage)} alt={live.title} />
        ) : (
          <div className="placeholder-photo" />
        )}
        <div className="grade" />
        <div className="top">
          {live.status === 'live' ? (
            <span className="live-pill"><span className="dot" /> LIVE</span>
          ) : live.status === 'paused' ? (
            <span className="live-pill" style={{ background: '#D7770F', boxShadow: 'none' }}>IN PAUSA</span>
          ) : (
            <span className="live-pill" style={{ background: '#5A655E', boxShadow: 'none' }}>
              {live.status === 'ended' ? 'CONCLUSA' : 'IN PREPARAZIONE'}
            </span>
          )}
          <span className="viewers-pill">
            {stackAvatars.length > 0 && (
              <span className="av-stack">
                {stackAvatars.map((a, i) => <span key={i}>{a}</span>)}
              </span>
            )}
            <Eye width={14} height={14} style={{ opacity: 0.85 }} />
            {compact(viewersCount)} in ascolto
          </span>
        </div>
        <div className="meta-bottom">
          <div className="eyebrow"><span className="bar" /> {kicker}</div>
          <h1>{live.title}</h1>
          {live.subtitle && <p className="sub">{live.subtitle}</p>}
        </div>
        <div className="float-layer" ref={floatRef} />
      </div>

      <aside className="hero-side">
        <div className="status-row">
          <span className="label">Durata trasmissione</span>
          {live.status === 'live' && (
            <span className="in-onda"><span className="dot" /> IN ONDA</span>
          )}
          {live.status === 'paused' && (
            <span className="in-onda" style={{ color: '#D7770F' }}>IN PAUSA</span>
          )}
        </div>
        <div className="elapsed">
          {formatElapsed(elapsed)}
          <small>h:m:s</small>
        </div>

        <div className="stats-grid">
          <div className="stat-tile accent">
            <div className="n">{compact(viewersCount)}</div>
            <div className="l">Spettatori connessi</div>
          </div>
          <div className="stat-tile">
            <div className="n">{compact(totalReactions)}</div>
            <div className="l">Reazioni totali</div>
          </div>
        </div>

        <div className="reaction-bar" role="group" aria-label="Reagisci all'evento (clicca quante volte vuoi)">
          {HERO_REACTIONS.map(k => {
            const n = reactionsCounts[k] || 0;
            const canReact = isAuthenticated && live.status === 'live';
            const title = !isAuthenticated
              ? 'Accedi come associato per reagire'
              : live.status !== 'live'
                ? 'La diretta non è in onda'
                : `${REACTION_LABEL[k]} (puoi cliccare più volte)`;
            return (
              <button
                key={k}
                type="button"
                onClick={() => handleReact(k)}
                disabled={!canReact}
                title={title}
              >
                <span>{REACTION_EMOJI[k]}</span>
                <span className="count">{compact(n)}</span>
              </button>
            );
          })}
        </div>

        <div className="actions">
          <button type="button" className="btn-share" onClick={share}>
            <Share2 width={16} height={16} /> Condividi la diretta
          </button>
          <button type="button" className="btn-icon" disabled title="Disponibile a breve">
            <Bookmark width={16} height={16} />
          </button>
        </div>
      </aside>
    </section>
  );
};

export default LiveHero;
